<template lang="pug">
  #discussion
    .level
      .level-left
        WtTitle(card-title) {{ title }}
      .level-right
        BCheckboxButton(
          v-model="photosOnly"
          size="is-small"
          :native-value="true"
        ) {{ photosOnlyText }}
        BTooltip(
          v-if="isJoined"
          :label="subscribeTooltipText"
          position="is-bottom"
          multilined
          animated
        )
          BCheckboxButton(
            v-model="subscribedToEmails"
            size="is-small"
            :native-value="true"
          ) {{ subscribeText }}
    article.media(v-if="showDiscussionInput")
      EntityAvatar.input-avatar(:image="member.avatar || false" size-class="is-64x64")
      .media-content
        .content
          BField(:type="{ 'is-danger': errors.has('discussion') }"
            :message="errors.first('discussion')")
              BInput(
                v-model="newPost"
                name="discussion"
                maxlength="1000"
                type="textarea"
                v-validate.disable="'required'"
              )
          .columns
            .column
              BField
                WtButton(
                  v-show="!showPhotoUpload"
                  @click.native="showPhotoUpload = !showPhotoUpload"
                  outlined
                ) Add A Photo!
                Transition(name="expand")
                  .columns(v-show="showPhotoUpload")
                    .column.level
                      .level-item(v-if="!showReplace")
                        BField
                          BUpload(
                            multiple
                            drag-drop
                            native
                            @input="previewImage"
                            accept="image/jpeg,image/gif,image/png"
                          )
                            .uploader-content.level(ref="uploader")
                              .level-left
                                .level-item
                                  BIcon(icon="upload" size="is-medium")
                              .level-right
                                .level-item
                                  .has-text-grey.is-size-7 Drop your files here or click to upload.
                      BField.level(v-else)
                        .level-item
                          figure.image.is-128x128
                            img(:src="previewImageData" alt="Your photo.")
                    .column.is-3(v-show="showReplace")
                      .columns.is-flex.is-multiline.is-vcentered
                        .column
                          BField
                            WtButton(
                              color="green"
                              outlined
                              size="small"
                              fullwidth
                              @click.native="replaceFile"
                            ) REPLACE
            .column.is-narrow
              WtButton(
                @click.native="submit"
                type="submit"
                :loading="submitting"
              ) Submit
    .has-text-danger(v-else-if="teamDiscussionClosed") Team has ended, discussion is closed.
    .has-text-danger(v-else-if="!isJoined && !$auth.user().isAdmin") Only team members can contribute to discussion.

    span(
      v-if="!_.isEmpty(dataObject.data)"
      v-for="item in dataObject.data"
      :key="item.id"
    )
      .media
      WtMediaObject(
        type="discussion"
        :data="item"
        :team-id="team.id"
        :page-info="{ photosOnly, currentPage }"
        :delete-discussion-post-action="deleteDiscussionPostAction"
      )
    .empty-state(v-if="_.isEmpty(dataObject.data) && teamDiscussionLoaded && isJoined")
      p Hey there,
      p Program wide posts will show here.
      p Currently, there are no posts.
    WtPagination(
      :pagination-object="dataObject.pagination"
      :action="fetchItems"
    )

</template>

<script>
import { mapActions, mapGetters } from "vuex";
import imageCompression from "browser-image-compression";
import WtMediaObject from "@@/WtMediaObject/WtMediaObject";
import WtTitle from "@@/WtTitle/WtTitle";
import WtPagination from "@@/WtPagination/WtPagination";
import WtButton from "@@/WtButton/WtButton";
import EntityAvatar from "@@/WtEntityAvatar/WtEntityAvatar";

export default {
  name: "TeamDiscussion",

  components: {
    EntityAvatar,
    WtMediaObject,
    WtTitle,
    WtPagination,
    WtButton
  },

  props: {
    dataObject: {
      type: Object,
      default: null,
      required: true
    },

    title: {
      type: String,
      default: "Discussion"
    },

    fetchAction: {
      type: Function,
      default(photosOnly = false, page = null) {
        return this.fetchTeamDiscussion({
          teamId: this.team.id,
          photosOnly,
          page
        });
      }
    },

    team: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      photosOnly: false,
      newPost: "",
      newPhoto: [],
      previewImageData: "",
      showReplace: false,
      replacing: false,
      submitting: false,
      showPhotoUpload: false,
      subscriptions: [],
      dataURL: null,
      imageCompressionError: false
    };
  },

  computed: {
    ...mapGetters("members", ["member", "memberTeams", "memberTeamsLoaded"]),
    ...mapGetters("teams", ["teamDiscussionLoaded"]),

    currentPage() {
      return this._.get(this.dataObject, "pagination.currentPage", 1);
    },

    showDiscussionInput() {
      if (this.$auth.user().isAdmin || this.isJoined) {
        return (
          this.currentPage === 1 &&
          !this.photosOnly &&
          !this.teamDiscussionClosed
        );
      }
      return false;
    },

    isJoined() {
      const memberTeamsIds = this.memberTeams.map(item => item.id);

      return memberTeamsIds.includes(this.team.id);
    },

    isSubscribed() {
      return this.subscriptions.includes(this.$auth.user().id);
    },

    teamDiscussionClosed() {
      return false;
    },

    subscribedToEmails: {
      set(val) {
        this.teamDiscussionSubscribeAction({
          topicId: this.team.teamTopicId,
          subscribe: val ? 1 : 0
        }).then(response => {
          if (response && response.data.success) {
            const message = response.data.data.subscribed
              ? "You will now receive emails when new posts are added to this discussion."
              : "You will no longer receive emails for discussion posts.";
            this.snackBar({
              type: "is-success",
              message
            });
            this.getSubscriptions();
          }
        });
      },
      get() {
        return this.isSubscribed;
      }
    },

    photosOnlyText() {
      if (this.photosOnly) {
        return "View All Posts";
      }
      return `View posts with photos`;
    },

    subscribeText() {
      if (this.subscribedToEmails) {
        return "Subscribed to discussion";
      }
      return `Subscribe to this discussion`;
    },

    subscribeTooltipText() {
      if (this.subscribedToEmails) {
        return "You are currently receiving emails for posts to this discussion.";
      }
      return `Receive emails when posts are made to this discussion.`;
    }
  },

  watch: {
    photosOnly() {
      return this.fetchItems();
    },

    team(newTeamObject, oldTeamObject) {
      // for when refresh parent page and child does not receive data immediately
      if (this._.isEmpty(oldTeamObject) && newTeamObject.teamTopicId) {
        this.getSubscriptions();
      }
    }
  },

  async mounted() {
    if (this.isJoined) {
      await this.fetchTeamDiscussion({ teamId: this.team.id });
    }
    if (this.team && this.team.teamTopicId) {
      this.getSubscriptions();
    }
  },

  beforeDestroy() {
    this.clearTeamDiscussion();
  },

  methods: {
    ...mapActions("members", ["fetchMemberTeams"]),
    ...mapActions("teams", [
      "fetchTeamDiscussion",
      "updateTeamDiscussion",
      "storeTeamDiscussion",
      "deleteTeamDiscussionPost",
      "deleteTeamDiscussionPhoto",
      "fetchTeamDiscussionSubscriptions",
      "teamDiscussionSubscribeAction",
      "clearTeamDiscussion"
    ]),
    ...mapActions(["snackBar"]),

    submit() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.storeNewDiscussion();
        }
      });
    },

    async storeNewDiscussion() {
      this.submitting = true;

      const formData = new FormData();
      if (this.newPhoto) formData.append("image", this.newPhoto);
      formData.append("text", this.newPost);
      formData.append("teamId", this.team.id);

      const payload = {
        formData
      };

      let response = null;

      response = await this.storeTeamDiscussion(payload);

      if (response.error) {
        if (
          response.error.code === "validation_failed" &&
          this._.has(response, "error.fields.image")
        ) {
          this.snackBar({
            type: "is-danger",
            duration: 10000,
            queue: false,
            message: response.error.fields.image[0]
          });
        } else {
          this.snackBar({
            type: "is-danger",
            duration: 10000,
            queue: false,
            message: `There was an error processing your entry.
                        Please try again. Or please reach out to wellness@be-engaged.co.`
          });
        }
      } else {
        this.snackBar({
          message: `Thank you for contributing to the discussion!`
        });
      }

      this.newPhoto = [];
      this.previewImageData = "";
      this.newPost = "";
      this.showReplace = false;
      this.replacing = false;
      this.submitting = false;
      this.showPhotoUpload = false;

      this.fetchItems();
    },

    async deleteDiscussionPostAction(discussionPostId) {
      this.$buefy.dialog.confirm({
        title: "Delete Discussion Post",
        confirmText: "Delete Discussion Post",
        type: "is-danger",
        hasIcon: true,
        message: "Are you sure you want to delete this discussion post?",
        onConfirm: async () => {
          await this.deleteTeamDiscussionPost(discussionPostId);
          this.fetchItems();
        }
      });
    },

    async previewImage(file) {
      file = file[0] || false;

      const options = {
        maxSizeMB: 10, // apiv3 maxsize
        maxWidthOrHeight: 1920,
        useWebWorker: true
      };
      let blob = null;
      try {
        blob = await imageCompression(file, options);

        this.dataURL = await imageCompression.getDataUrlFromFile(blob);

        this.newPhoto = blob;

        this.showReplace = true;
        this.previewImageData = this.dataURL;
      } catch (error) {
        this.imageCompressionError = true;
      }
    },

    fetchItems(event) {
      return this.fetchAction(this.photosOnly, event);
    },
    replaceFile() {
      this.replacing = true;
      this.showReplace = false;
      this.previewImageData = "";
      this.newPhoto = null;

      this.$nextTick(() => {
        if (this.$refs.uploader) {
          this.$refs.uploader.value = null;
          this.$refs.uploader.click();
        } else {
          console.error("File uploader ref is not available");
        }
      });
    },

    async getSubscriptions() {
      this.subscriptions = await this.fetchTeamDiscussionSubscriptions(
        this.team.teamTopicId
      );
    }
  }
};
</script>

<style lang="sass" scoped>
#discussion
  .uploader-content
    padding: 3rem
  .expand-enter-active
    transition: all .5s
  .expand-enter
    height: 0
    opacity: 0
  .expand-enter-to
    height: auto
    opacity: auto
  .input-avatar
    margin-right: 1.2rem
</style>
